/** Bamburgh chrome tokens — match layout Sass, not MUI theme breakpoints. */
export const SIDEBAR_WIDTH = 290
export const SIDEBAR_WIDTH_COLLAPSED = 100
export const HEADER_HEIGHT = 78
/** Permanent drawer at md+ (same off-canvas split as the old slide). */
export const DESKTOP_NAV_MIN_PX = 768
export const DESKTOP_NAV_QUERY = `(min-width:${DESKTOP_NAV_MIN_PX}px)`
export const DESKTOP_NAV_MQ = `@media (min-width: ${DESKTOP_NAV_MIN_PX}px)`
export const MOBILE_NAV_MQ = `@media (max-width: ${DESKTOP_NAV_MIN_PX - 0.02}px)`
/** Hamburger remains visible below 950px (existing `showBelowMl` behavior). */
export const HAMBURGER_NAV_MAX_PX = 949.98
/** Public marketplace nav (in-flow `masthead smaller`). */
export const PUBLIC_NAV_HEIGHT = 75
export const PAGE_BG = '#f4f5fd'
export const SIDEBAR_DARK_BG = '#253053'
export const CHROME_TRANSITION =
  'all 0.5s cubic-bezier(0.685, 0.0473, 0.346, 1)'
export const HEADER_SHADOW =
  '0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)'
export const SIDEBAR_SHADOW =
  '0.46875rem 0 2.1875rem rgba(59, 62, 102, 0.03), 0.9375rem 0 1.40625rem rgba(59, 62, 102, 0.03), 0.25rem 0 0.53125rem rgba(59, 62, 102, 0.05), 0.125rem 0 0.1875rem rgba(59, 62, 102, 0.03)'

/** Nested sx key — ColorModeProvider still sets html[data-color-mode]. */
export const DARK_MODE_SEL = 'html[data-color-mode="dark"] &'
export const chromePageDarkSx = {
  backgroundColor: '#121212 !important',
  color: 'rgba(255, 255, 255, 0.87)',
}
export const chromeHeaderDarkSx = {
  backgroundColor: '#1e1e1e !important',
  color: 'rgba(255, 255, 255, 0.87)',
}
export const chromeSidebarDarkSx = {
  backgroundColor: '#1e1e1e !important',
}

/** MUI sx treats numeric margin/padding as spacing units (×8). Always pass px. */
const px = (n) => `${n}px`

export const wrapperSx = {
  display: 'flex',
  flex: 1,
  backgroundColor: PAGE_BG,
  overflowX: 'hidden',
  minHeight: '100vh',
  width: '100%',
  transition: CHROME_TRANSITION,
  [DARK_MODE_SEL]: chromePageDarkSx,
}

export const mainSx = {
  display: 'flex',
  flex: '1 1 auto',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flexDirection: 'column',
  position: 'relative',
  minHeight: '100vh',
  backgroundColor: PAGE_BG,
  boxSizing: 'border-box',
  [DARK_MODE_SEL]: chromePageDarkSx,
}

/**
 * Main column. Permanent Drawer occupies 290/100px in the flex row, so this
 * is flex:1 — not the old overlay + marginLeft Bamburgh offset.
 */
export function mainOffsetSx({ sidebarFixed, collapsed } = {}) {
  void sidebarFixed
  void collapsed
  return mainSx
}

export function contentSx({ headerFixed } = {}) {
  return {
    flex: '1 1 auto',
    display: 'block',
    position: 'relative',
    zIndex: 1,
    boxSizing: 'border-box',
    width: '100%',
    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
    backgroundColor: PAGE_BG,
    overflow: 'auto',
    transition: CHROME_TRANSITION,
    paddingTop: headerFixed ? px(HEADER_HEIGHT) : 0,
    [DARK_MODE_SEL]: chromePageDarkSx,
    [MOBILE_NAV_MQ]: {
      zIndex: 500,
      paddingTop: headerFixed ? px(HEADER_HEIGHT) : 0,
    },
  }
}

export function contentInnerSx({ compact } = {}) {
  return {
    overflow: 'visible',
    padding: compact ? '0.5rem' : '3rem',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: `calc(100vh - ${HEADER_HEIGHT}px - 6rem)`,
    '@media (max-width: 575.98px)': compact
      ? {}
      : { padding: '1rem' },
  }
}

/** Property/lot dashboards fill the chrome below the header — no page padding. */
export const fullBleedContentInnerSx = {
  overflow: 'hidden',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  boxSizing: 'border-box',
  flex: '1 1 auto',
  minHeight: 0,
  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
}

export const contentInnerWrapperSx = {
  display: 'block',
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: '20rem',
}

export const fullBleedContentInnerWrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flex: '1 1 auto',
  minHeight: 0,
  height: '100%',
}

/**
 * Public marketplace shell. Admin chrome is a row (drawer + main).
 * PublicHeader is in-flow (not the admin fixed bar) so listings and
 * proforma sit under the nav at full width instead of a tall right column.
 */
export const publicWrapperSx = {
  ...wrapperSx,
  display: 'flex',
  flexDirection: 'column !important',
  alignItems: 'stretch',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
}

export const publicHeaderRootSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flex: '0 0 auto',
  alignSelf: 'stretch',
}

export function publicHeaderBarSx(isHome = false) {
  return {
    position: 'relative !important',
    left: 'auto !important',
    top: 'auto !important',
    width: '100%',
    // Home hero sizes to the headline. A fixed 20em + overflow:hidden
    // flex-shrinks the intro and stacks wrapped words on top of each other.
    height: isHome ? 'auto' : px(PUBLIC_NAV_HEIGHT),
    minHeight: isHome ? '20em' : px(PUBLIC_NAV_HEIGHT),
    overflow: isHome ? 'visible' : 'hidden',
    zIndex: 999,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: isHome ? 'flex-start' : 'center',
    color: '#fff',
    backgroundImage: 'none',
    // Inner pages keep white Agency nav text; home uses the hero overlay instead.
    ...(!isHome
      ? {
          backgroundColor: SIDEBAR_DARK_BG,
          px: '1rem',
        }
      : {}),
  }
}

export function isPublicProformaHref(href = '') {
  return href.includes('/proforma/')
}

export const publicContentSx = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: 0,
  paddingTop: 0,
  paddingLeft: 0,
  backgroundColor: PAGE_BG,
  overflow: 'auto',
  [DARK_MODE_SEL]: chromePageDarkSx,
}

export const publicContentInnerSx = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: 0,
  padding: '1.5rem',
  boxSizing: 'border-box',
  overflow: 'visible',
  '@media (max-width: 575.98px)': { padding: '1rem' },
}

export const publicContentInnerWrapperSx = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: 0,
}

/** Same remaining-viewport fill as logged-in `fullBleedContentInnerSx`. */
export const publicProformaContentSx = {
  ...publicContentSx,
  overflow: 'hidden',
  height: `calc(100vh - ${PUBLIC_NAV_HEIGHT}px)`,
  minHeight: `calc(100vh - ${PUBLIC_NAV_HEIGHT}px)`,
}

export const publicFullBleedContentInnerSx = {
  ...fullBleedContentInnerSx,
  height: '100%',
}

export const publicFullBleedContentInnerWrapperSx = {
  ...fullBleedContentInnerWrapperSx,
}

/** Auth / share-link pages that used app-wrapper + app-main + app-content. */
export const pageShellWrapperSx = {
  ...wrapperSx,
  backgroundColor: '#fff',
  minHeight: '100vh',
}

export const pageShellMainSx = {
  ...mainSx,
  minHeight: '100vh',
}

export const pageShellContentSx = {
  flex: 1,
  display: 'flex',
  padding: 0,
  flexDirection: 'column',
  transition: CHROME_TRANSITION,
}

export const pageShellInnerSx = {
  ...contentInnerSx(),
  display: 'flex',
  alignItems: 'center',
}

/**
 * MUI Drawer root: in-flow width on desktop (content offset), zero on mobile
 * so the temporary variant overlays instead of reserving space.
 */
export function drawerRootSx({ collapsed = false, isDesktop = true } = {}) {
  const paperBase = {
    boxSizing: 'border-box',
    border: 'none',
    backgroundImage: 'none',
    overflow: 'visible',
  }
  if (!isDesktop) {
    return {
      width: 0,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        ...paperBase,
        width: SIDEBAR_WIDTH,
      },
    }
  }
  return {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    '@media (min-width: 950px)': collapsed
      ? { width: SIDEBAR_WIDTH_COLLAPSED }
      : {},
    '& .MuiDrawer-paper': {
      ...paperBase,
      width: SIDEBAR_WIDTH,
      '@media (min-width: 950px)': collapsed
        ? { width: SIDEBAR_WIDTH_COLLAPSED }
        : {},
    },
  }
}

/** Header AppBar — always fixed; desktop width offset matches permanent Drawer. */
export function headerBarSx({
  collapsed,
  headerShadow,
  headerBgTransparent,
} = {}) {
  return {
    height: HEADER_HEIGHT,
    px: '1rem',
    backgroundColor: headerBgTransparent
      ? 'rgba(255, 255, 255, 0.95)'
      : '#fff',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: 'inherit',
    zIndex: 1201,
    transition: CHROME_TRANSITION,
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: headerShadow ? HEADER_SHADOW : 'none',
    [DARK_MODE_SEL]: chromeHeaderDarkSx,
    width: '100%',
    ml: 0,
    [DESKTOP_NAV_MQ]: {
      width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
      ml: px(SIDEBAR_WIDTH),
    },
    '@media (min-width: 950px)': collapsed
      ? {
          width: `calc(100% - ${SIDEBAR_WIDTH_COLLAPSED}px)`,
          ml: px(SIDEBAR_WIDTH_COLLAPSED),
        }
      : {},
  }
}
